// nodeModule.js — Knowledge (public) vs Collections (private) node membership

import { normalizeBaseVisibility, BASE_VISIBILITY } from 'utilities/baseVisibility';

export const NODE_MODULE = Object.freeze({
  PRIVATE: 'private',
  PUBLIC: 'public',
});

/**
 * Normalizes a raw module value to private | public.
 *
 * @param {unknown} value
 * @returns {'private'|'public'|null} Canonical module, or null if unset/invalid.
 */
export function normalizeNodeModule(value) {
  const cleaned = String(value || '').trim().toLowerCase();
  if (cleaned === NODE_MODULE.PUBLIC) return NODE_MODULE.PUBLIC;
  if (cleaned === NODE_MODULE.PRIVATE) return NODE_MODULE.PRIVATE;
  return null;
}

/**
 * Resolves effective module for a node, with legacy base-visibility fallback.
 *
 * @param {object|null|undefined} node
 * @param {object|null|undefined} [base] - Base/base row (for migration fallback).
 * @returns {'private'|'public'}
 */
export function resolveNodeModule(node, base = null) {
  const fromNode = normalizeNodeModule(node?.module);
  if (fromNode) return fromNode;

  // Pre-migration: public bases were full Chat corpus.
  if (normalizeBaseVisibility(base?.visibility) === BASE_VISIBILITY.PUBLIC) {
    return NODE_MODULE.PUBLIC;
  }

  return NODE_MODULE.PRIVATE;
}

/**
 * @param {object|null|undefined} node
 * @param {object|null|undefined} [base]
 * @returns {boolean}
 */
export function isKnowledgeNode(node, base = null) {
  return resolveNodeModule(node, base) === NODE_MODULE.PUBLIC;
}

/**
 * @param {object|null|undefined} node
 * @param {object|null|undefined} [base]
 * @returns {boolean}
 */
export function isCollectionsNode(node, base = null) {
  return resolveNodeModule(node, base) === NODE_MODULE.PRIVATE;
}

/**
 * Ensures a create payload has an immutable module.
 *
 * @param {object} payload
 * @param {'private'|'public'|null|undefined} preferredModule
 * @param {object|null|undefined} [base]
 * @returns {object}
 */
export function withNodeModule(payload, preferredModule = null, base = null) {
  const next = payload && typeof payload === 'object' ? { ...payload } : {};
  const preferred = normalizeNodeModule(preferredModule);
  if (preferred) {
    next.module = preferred;
    return next;
  }

  next.module = resolveNodeModule(next, base);
  return next;
}
